import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './RedType.css';

gsap.registerPlugin(ScrollTrigger);

const PROMPT = '두 색(#F90000 + #14181E)만으로 GSAP ScrollTrigger로 텍스트 글자 단위 빌드업 + 마우스 위치에 따라 배경 그라디언트가 shift되는 원페이지. Three.js 없이 CSS + GSAP만으로 몰입감 실험.';

const GRID_TEXT = 'DESIGN IS NOT JUST WHAT IT LOOKS LIKE AND FEELS LIKE DESIGN IS HOW IT WORKS';

const RedType: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const gradientRef = useRef<HTMLDivElement>(null);
  const mousePos = useRef({ x: 0.5, y: 0.5 });
  const exp = experiments.find(e => e.id === 'red-type');

  // Mouse tracking for gradient + custom cursor
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      mousePos.current = { x, y };

      if (cursorRef.current) {
        cursorRef.current.style.left = `${e.clientX}px`;
        cursorRef.current.style.top = `${e.clientY}px`;
      }

      if (gradientRef.current) {
        const angle = Math.round(x * 360);
        const intensity = Math.round(y * 15 + 5);
        gradientRef.current.style.background = `radial-gradient(circle at ${x * 100}% ${y * 100}%, rgba(249,0,0,0.${intensity}) 0%, transparent 60%), linear-gradient(${angle}deg, rgba(249,0,0,0.04) 0%, transparent 50%)`;
      }
    };

    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  // GSAP ScrollTrigger animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero — character-by-character build (plays immediately on load)
      gsap.to('.heroTitle .char', {
        opacity: 1,
        y: 0,
        rotateX: 0,
        stagger: 0.04,
        duration: 0.8,
        ease: 'power3.out',
        delay: 0.3,
      });

      gsap.to('.heroSubline', {
        opacity: 1,
        duration: 1,
        delay: 1,
      });

      // Divider
      gsap.to('.redDivider', {
        opacity: 0.6,
        width: '200px',
        duration: 1,
        scrollTrigger: {
          trigger: '.redDivider',
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      });

      // Manifesto — fill each character on scroll
      document.querySelectorAll('.manifestoLine').forEach((line) => {
        const chars = line.querySelectorAll('.char');
        gsap.to(chars, {
          opacity: 1,
          className: '+=filled',
          stagger: 0.03,
          scrollTrigger: {
            trigger: line,
            start: 'top 75%',
            end: 'top 25%',
            scrub: 1,
          },
        });
      });

      // Counter — scale in
      gsap.to('.counterNumber', {
        opacity: 1,
        scale: 1,
        duration: 1.2,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.counterSection',
          start: 'top 60%',
          toggleActions: 'play none none reverse',
        },
      });

      gsap.to('.counterLabel', {
        opacity: 0.6,
        duration: 1,
        delay: 0.3,
        scrollTrigger: {
          trigger: '.counterSection',
          start: 'top 50%',
          toggleActions: 'play none none reverse',
        },
      });

      // Grid — reveal chars in wave
      gsap.to('.gridChar', {
        className: '+=revealed',
        stagger: {
          each: 0.02,
          from: 'random',
        },
        scrollTrigger: {
          trigger: '.gridSection',
          start: 'top 60%',
          end: 'bottom 60%',
          scrub: 1,
        },
      });

      // Closing — words fly in
      gsap.to('.closingText .word', {
        opacity: 1,
        y: 0,
        stagger: 0.15,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: '.closingSection',
          start: 'top 60%',
          toggleActions: 'play none none reverse',
        },
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const splitChars = (text: string) =>
    text.split('').map((ch, i) => (
      <span key={i} className="char">
        {ch === ' ' ? '\u00A0' : ch}
      </span>
    ));

  const splitWords = (text: string) =>
    text.split(' ').map((word, i) => (
      <span key={i} className="word" style={{ transform: 'translateY(30px)' }}>
        {word}
      </span>
    ));

  return (
    <div ref={containerRef} className="redType">
      <div ref={cursorRef} className="redCursor" />
      <div ref={gradientRef} className="gradientBg" />
      <div className="noiseOverlay" />

      <ExperimentNav prompt={exp?.prompt || PROMPT} title="Red Type" />

      {/* Section 1 — Hero */}
      <section className="redSection heroSection">
        <h1 className="heroTitle">{splitChars('RED')}</h1>
        <h1 className="heroTitle">{splitChars('TYPE')}</h1>
        <p className="heroSubline">two colors — infinite depth</p>
      </section>

      <div className="redDivider" />

      {/* Section 2 — Manifesto */}
      <section className="redSection manifestoSection">
        <p className="manifestoLine">{splitChars('We build with')}</p>
        <p className="manifestoLine">{splitChars('constraints.')}</p>
        <p className="manifestoLine">{splitChars('Two colors.')}</p>
        <p className="manifestoLine">{splitChars('One typeface.')}</p>
        <p className="manifestoLine">{splitChars('Zero limits.')}</p>
      </section>

      {/* Section 3 — Counter */}
      <section className="redSection counterSection">
        <div className="counterNumber" style={{ transform: 'scale(0.8)' }}>02</div>
        <div className="counterLabel">colors used</div>
      </section>

      {/* Section 4 — Grid */}
      <section className="redSection gridSection">
        <div className="letterGrid">
          {GRID_TEXT.split('').map((ch, i) => (
            <div key={i} className="gridChar">
              {ch === ' ' ? '' : ch}
            </div>
          ))}
        </div>
      </section>

      {/* Section 5 — Closing */}
      <section className="redSection closingSection">
        <div className="closingText">
          {splitWords('Less is more when more is noise.')}
          <span className="closingCursor" />
        </div>
      </section>
    </div>
  );
};

export default RedType;
